import { useMemo, useCallback, useState, useEffect } from "react";
import type { Row, CellTypes, IColumnDefsConfig } from "@focus/focusUI";
import { OPERATORCOLUMN } from "../../helpers/utils";
import type { ColumnOrderState } from "./Table";
import { type TableActionType, TableActionsColumn } from "./TableActionColumn";
import { useDebounced } from "../../hooks/useDebounced";
import {
  buildColumnLayoutState,
  buildServerColumnConfig,
  type ColumnLayoutState,
  type ServerColumnConfig,
} from "../../services/helpers/headers";

const ACTIONS_COLUMN_KEY = "actions";

export type Updater<T> = T | ((old: T) => T);
export type OnChangeFn<T> = (updaterOrValue: Updater<T>) => void;

export type RowData = unknown | object | any[];
export interface FilterMeta {}
export type TransformFilterValueFn<TData extends RowData> = (
  value: any,
  column?,
) => unknown;
export type ColumnFilterAutoRemoveTestFn<TData extends RowData> = (
  value: any,
  column?,
) => boolean;
export interface FilterFn<TData extends RowData> {
  (
    row: Row<TData>,
    columnId: string,
    filterValue: any,
    addMeta: (meta: FilterMeta) => void,
  ): boolean;
  resolveFilterValue?: TransformFilterValueFn<TData>;
  autoRemove?: ColumnFilterAutoRemoveTestFn<TData>;
}

export type ToggleVisibilityArgs =
  | { id: string }
  | { ids: string[]; visible: boolean };

interface UseFocusTableParams {
  serverDisplayConfig: ServerColumnConfig;
  defaultVisibleValues: string[];
  columnDefinitions: IColumnDefsConfig[];
  actions: TableActionType[];
  base: string;
  source?: string;
  downloadData?: (param: { id: string }) => void;
  saveColumnLayout?: (config: ServerColumnConfig) => void;
  showOperatorColumn?: boolean;
}

export const useFocusocTable = ({
  serverDisplayConfig,
  defaultVisibleValues,
  columnDefinitions,
  actions,
  base,
  source,
  downloadData,
  saveColumnLayout,
  showOperatorColumn = true,
}: UseFocusTableParams) => {
  const { visibility, order, columns } = buildColumnLayoutState(
    serverDisplayConfig,
    defaultVisibleValues,
    columnDefinitions as {
      headerName: string;
      key: string;
      hide: boolean;
      customValueGetter: ({ row, key }: { row: any; key: any }) => any;
      cellType: CellTypes;
    }[],
  );

  const hasActions = actions && actions.length > 0;
  const actionsColumnMinSize = Math.max(150, (actions?.length ?? 0) * 34 + 32);

  /**
   * Actions column definition
   */
  const actionsColumn: IColumnDefsConfig = useMemo(
    () => ({
      headerName: "Actions",
      key: ACTIONS_COLUMN_KEY,
      enableColumnFilter: false,
      enableSorting: false,
      minSize: actionsColumnMinSize,
      customCellRenderer: (rowData) =>
        TableActionsColumn({
          context: { actions, base, source, downloadData },
          data: rowData,
        }),
    }),
    [actions, actionsColumnMinSize, base, source, downloadData],
  );

  const normalizeLayout = (
    { visibility, order, columns }: ColumnLayoutState,
  ): ColumnLayoutState => {
    let nextVisibility = { ...visibility };
    let nextOrder = [...order];
    let nextColumns = [...columns];

    if (hasActions) {
      nextVisibility[ACTIONS_COLUMN_KEY] = true;

      nextColumns = nextColumns.filter((c) => c.key !== ACTIONS_COLUMN_KEY);
      nextColumns.unshift(actionsColumn);

      nextOrder = nextOrder.filter((id) => id !== ACTIONS_COLUMN_KEY);
      nextOrder = [ACTIONS_COLUMN_KEY, ...nextOrder];
    }

    if (showOperatorColumn) {
      nextVisibility[OPERATORCOLUMN.key] =
        nextVisibility[OPERATORCOLUMN.key] ?? true;

      if (!nextColumns.some((c) => c.key === OPERATORCOLUMN.key)) {
        nextColumns = [...nextColumns, OPERATORCOLUMN];
      }

      if (!nextOrder.includes(OPERATORCOLUMN.key)) {
        nextOrder.push(OPERATORCOLUMN.key);
      }
    }

    return {
      visibility: nextVisibility,
      order: nextOrder,
      columns: nextColumns,
    };
  };

  const [state, setState] = useState<ColumnLayoutState>(() =>
    normalizeLayout(
      {
        visibility,
        order,
        columns,
      },
    ),
  );

  const { update: debounceUpdate } = useDebounced<ColumnLayoutState>({
    initialValue: {
      visibility: state.visibility,
      order: state.order,
      columns: state.columns,
    },
    delay: 1000,
    onDebounce: (state) => {
      const serverConfig = buildServerColumnConfig(
        state.visibility,
        state.order,
        state.columns,
      );
      saveColumnLayout?.(serverConfig);
    },
  });

  const toggleVisibility = useCallback(
    (args: ToggleVisibilityArgs) => {
      setState((prev) => {
        const nextVisibility = { ...prev.visibility };

        if ("id" in args) {
          if (args.id === ACTIONS_COLUMN_KEY) return prev;
          nextVisibility[args.id] = !prev.visibility[args.id];
        }

        if ("ids" in args) {
          args.ids.forEach((id) => {
            if (id === ACTIONS_COLUMN_KEY) return;
            nextVisibility[id] = args.visible;
          });
        }

        return normalizeLayout(
          {
            ...prev,
            visibility: nextVisibility,
          },
        );
      });
    },
    [hasActions],
  );

  /**
   * Order handler
   */
  const handleColumnOrderChange = useCallback(
    (order: ColumnOrderState) => {
      setState((prev) =>
        normalizeLayout(
          {
            ...prev,
            order,
          },
        ),
      );
    },
    [hasActions],
  );

  const handleColumnResize = useCallback((columnId: string, width: number) => {
    setState((prev) =>
      normalizeLayout(
        {
          ...prev,
          columns: prev.columns.map((c) =>
            c.key === columnId ? { ...c, size: width, minSize: c.minSize } : c,
          ),
        },
      ),
    );
  }, []);

  const getSearchableRowText = (original: unknown) =>
    Object.values((original ?? {}) as Record<string, unknown>)
      .flat()
      .join(" ")
      .toLowerCase();

  const customGlobalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
    const search = String(filterValue).toLowerCase();
    // Group - search in subRows
    if (row.subRows?.length) {
      return row.subRows.some((r) =>
        getSearchableRowText(r.original).includes(search),
      );
    }

    // Normal Row (leaf row)
    return getSearchableRowText(row.original).includes(search);
  };

  useEffect(() => {
    debounceUpdate(() => ({
      visibility: state.visibility,
      order: state.order,
      columns: state.columns,
    }));
  }, [state.visibility, state.order, state.columns]);

  return {
    columnDefs: state.columns,
    columnVisibility: state.visibility,
    columnOrder: state.order,
    customGlobalFilterFn,
    toggleVisibility,
    handleColumnOrderChange,
    handleColumnResize,
  };
};
